import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Plus, 
  Pencil, 
  Trash2,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  { value: 'orange', label: 'Laranja', class: 'bg-orange-500' },
  { value: 'cyan', label: 'Ciano', class: 'bg-cyan-500' },
  { value: 'emerald', label: 'Verde', class: 'bg-emerald-500' },
  { value: 'amber', label: 'Âmbar', class: 'bg-amber-500' },
  { value: 'purple', label: 'Roxo', class: 'bg-purple-500' },
  { value: 'pink', label: 'Rosa', class: 'bg-pink-500' },
];

export default function SectorsPage() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: 'orange',
  });

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sectors`, { credentials: 'include' });
      if (response.ok) {
        setSectors(await response.json());
      }
    } catch (error) {
      console.error('Error fetching sectors:', error);
      toast.error('Erro ao carregar setores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const url = editingSector 
        ? `${API_URL}/api/sectors/${editingSector.sector_id}`
        : `${API_URL}/api/sectors`;
      
      const response = await fetch(url, {
        method: editingSector ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingSector ? 'Setor atualizado' : 'Setor criado');
        setDialogOpen(false);
        resetForm();
        fetchSectors();
      } else {
        toast.error('Erro ao salvar setor');
      }
    } catch (error) {
      console.error('Error saving sector:', error);
      toast.error('Erro ao salvar setor');
    }
  };

  const handleDelete = async (sectorId) => {
    try {
      const response = await fetch(`${API_URL}/api/sectors/${sectorId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Setor excluído');
        fetchSectors();
      } else {
        toast.error('Erro ao excluir setor');
      }
    } catch (error) {
      console.error('Error deleting sector:', error);
      toast.error('Erro ao excluir setor');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const confirmDelete = (sectorId) => {
    setDeleteTarget(sectorId);
    setDeleteConfirmOpen(true);
  };

  const handleEdit = (sector) => {
    setEditingSector(sector);
    setFormData({
      name: sector.name,
      color: sector.color,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSector(null);
    setFormData({ name: '', color: 'orange' });
  };

  const getColorClass = (color) => {
    const found = COLORS.find(c => c.value === color);
    return found?.class || 'bg-zinc-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="sectors-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight flex items-center gap-3">
              <Layers className="w-8 h-8" />
              Setores
            </h1>
            <p className="text-zinc-400 mt-1">Gerencie os setores da cozinha</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-white text-black hover:bg-zinc-200" data-testid="add-sector-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Setor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-100">
                  {editingSector ? 'Editar Setor' : 'Novo Setor'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nome do setor"
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="sector-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Cor</Label>
                  <Select 
                    value={formData.color} 
                    onValueChange={(v) => setFormData({...formData, color: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${getColorClass(formData.color)}`} />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {COLORS.map(color => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.class}`} />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleSubmit}
                  className="w-full bg-white text-black hover:bg-zinc-200"
                  data-testid="save-sector-btn"
                >
                  {editingSector ? 'Salvar Alterações' : 'Criar Setor'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sectors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 col-span-full">
              <CardContent className="py-12 text-center text-zinc-500">
                Nenhum setor cadastrado
              </CardContent>
            </Card>
          ) : (
            sectors.map((sector) => (
              <Card 
                key={sector.sector_id} 
                className="bg-zinc-900 border-zinc-800 overflow-hidden"
                data-testid={`sector-card-${sector.sector_id}`}
              >
                <div className={`h-2 ${getColorClass(sector.color)}`} />
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${getColorClass(sector.color)} bg-opacity-20 flex items-center justify-center`}>
                        <Layers className={`w-5 h-5 ${
                          sector.color === 'orange' ? 'text-orange-500' :
                          sector.color === 'cyan' ? 'text-cyan-500' :
                          sector.color === 'emerald' ? 'text-emerald-500' :
                          sector.color === 'amber' ? 'text-amber-500' :
                          sector.color === 'purple' ? 'text-purple-500' :
                          sector.color === 'pink' ? 'text-pink-500' :
                          'text-zinc-500'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-200">{sector.name}</h3>
                        <p className="text-xs text-zinc-500">
                          {COLORS.find(c => c.value === sector.color)?.label || 'Cor padrão'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(sector)}
                        className="h-8 w-8 text-zinc-400 hover:text-zinc-200"
                        data-testid={`edit-sector-${sector.sector_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => confirmDelete(sector.sector_id)}
                        className="h-8 w-8 text-zinc-400 hover:text-red-400"
                        data-testid={`delete-sector-${sector.sector_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-zinc-100">Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem certeza que deseja excluir este setor? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => deleteTarget && handleDelete(deleteTarget)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
